import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { createPortal } from "react-dom";
import { Phone, MessageCircle, ChevronLeft, ChevronRight, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Container, ColorBar, PageHeader, Picture, QuoteForm, ContactButtons, PHONE, waLink } from "@/components/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Variant detail dialog ───────────────────────────────────────────────── */
function VariantModal({ variant, onClose }) {
  const images = [variant.cover_image, ...(variant.gallery_images || [])].filter(Boolean);
  const [idx, setIdx] = useState(0);
  const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = overflow; };
  }, [prev, next, onClose]);

  const message = `Hi, I'm interested in ${variant.name}. Can you share more details?`;

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-ink/70 flex items-end sm:items-center justify-center sm:p-6" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="variant-title"
        className="bg-paper w-full sm:max-w-3xl max-h-[95vh] overflow-y-auto border border-ink sm:shadow-[10px_10px_0_0_#E5007D]"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 px-5 sm:px-6 py-4 bg-paper border-b border-ink">
          <h2 id="variant-title" className="font-display font-bold text-xl sm:text-2xl text-ink">{variant.name}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-2 -mr-2 hover:bg-ink hover:text-paper transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="grid sm:grid-cols-[1.3fr_1fr]">
          <div className="relative bg-[#ECEAE3] sm:border-r border-ink">
            <Picture src={images[idx]} alt={variant.name} className="w-full aspect-square" />
            {images.length > 1 && (
              <>
                <button type="button" onClick={prev} aria-label="Previous image"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-paper border border-ink flex items-center justify-center hover:bg-ink hover:text-paper transition-colors">
                  <ChevronLeft size={20} />
                </button>
                <button type="button" onClick={next} aria-label="Next image"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-paper border border-ink flex items-center justify-center hover:bg-ink hover:text-paper transition-colors">
                  <ChevronRight size={20} />
                </button>
                <div className="absolute bottom-3 inset-x-0 flex justify-center gap-2">
                  {images.map((img, i) => (
                    <button key={i} type="button" onClick={() => setIdx(i)} aria-label={`Image ${i + 1}`}
                      className={`w-11 h-11 border-2 overflow-hidden ${i === idx ? "border-process-magenta" : "border-paper opacity-70 hover:opacity-100"}`}>
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="p-5 sm:p-6 flex flex-col">
            {variant.starting_at > 0 && (
              <p className="font-display text-ink">
                <span className="block text-sm text-graphite">From</span>
                <span className="text-4xl font-bold tracking-tight">₹{variant.starting_at}</span>
              </p>
            )}
            {variant.description && <p className="mt-4 text-graphite leading-relaxed">{variant.description}</p>}
            <div className="mt-auto pt-8 flex flex-col gap-3">
              <a href={`tel:+${PHONE}`} data-testid="variant-call-btn" className="btn btn-ink"><Phone size={16} aria-hidden="true" /> Call us</a>
              <a href={waLink(message)} target="_blank" rel="noreferrer" data-testid="variant-whatsapp-btn" className="btn btn-whatsapp">
                <MessageCircle size={16} aria-hidden="true" /> Ask on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── Page ────────────────────────────────────────────────────────────────── */
export default function CategoryPublicPage() {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/categories/${categoryId}`),
      axios.get(`${API}/categories/${categoryId}/variants`),
    ])
      .then(([c, v]) => { setCategory(c.data); setVariants(v.data); })
      .catch(() => setCategory(null))
      .finally(() => setLoading(false));
  }, [categoryId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-paper">
        <Navbar />
        <div className="pt-40 pb-32 text-center text-graphite" role="status">Loading…</div>
        <Footer />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-paper">
        <Navbar />
        <PageHeader title="We couldn't find that product" lede="It may have been renamed or removed. Have a look at everything we print instead.">
          <Link to="/#services" className="btn btn-ink">See everything we print</Link>
        </PageHeader>
        <Footer />
      </div>
    );
  }

  const message = `Hi, I'm interested in ${category.name}. Can you share more details?`;

  return (
    <div className="min-h-screen bg-paper">
      <Navbar />

      <header className="pt-28 md:pt-32 border-b border-ink">
        <Container className="pb-12 md:pb-16">
          <nav aria-label="Breadcrumb" className="mb-8 text-sm text-graphite">
            <Link to="/" className="hover:text-ink hover:underline underline-offset-4">Home</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <Link to="/#services" className="hover:text-ink hover:underline underline-offset-4">What we print</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span className="text-ink">{category.name}</span>
          </nav>
          <div className="grid gap-10 md:grid-cols-2 md:gap-14 items-start">
            <Picture src={category.image} alt={category.name} className="w-full aspect-square border border-ink shadow-[10px_10px_0_0_#FFED00]" />
            <div className="md:pt-4">
              <h1 className="font-display font-bold text-5xl sm:text-6xl tracking-[-0.035em] leading-[1] text-ink">{category.name}</h1>
              {category.starting_price > 0 && (
                <p className="mt-6 font-display text-ink">
                  <span className="text-graphite">Starting at </span>
                  <span className="text-3xl font-bold">₹{category.starting_price}</span>
                </p>
              )}
              {category.description && <p className="mt-6 text-lg text-graphite leading-relaxed max-w-[60ch]">{category.description}</p>}
              <ContactButtons className="mt-8" message={message} />
            </div>
          </div>
        </Container>
        <ColorBar />
      </header>

      <main>
        {variants.length > 0 && (
          <section className="py-16 md:py-24" aria-labelledby="variants">
            <Container>
              <h2 id="variants" className="font-display font-bold text-3xl sm:text-5xl text-ink">Choose a finish</h2>
              <p className="mt-3 text-lg text-graphite">{variants.length} {variants.length === 1 ? "option" : "options"}. Open one for photos and prices.</p>
              <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {variants.map((v) => (
                  <li key={v.id}>
                    <button type="button" onClick={() => setActive(v)} data-testid={`view-variant-${v.id}`}
                      className="group w-full h-full text-left flex flex-col bg-stock border border-ink transition-[transform,box-shadow] duration-150 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#00A3E0]">
                      <Picture src={v.cover_image} alt={v.name} className="w-full aspect-[4/3] border-b border-ink" />
                      <span className="p-5 flex flex-1 flex-col">
                        <span className="font-display font-bold text-xl text-ink">{v.name}</span>
                        {v.description && <span className="mt-2 text-graphite leading-relaxed line-clamp-2">{v.description}</span>}
                        <span className="mt-auto pt-5 flex items-end justify-between">
                          {v.starting_at > 0 ? <span className="font-display text-2xl font-bold text-ink">₹{v.starting_at}</span> : <span />}
                          <span className="font-display font-semibold text-ink underline underline-offset-4 decoration-[1.5px] group-hover:decoration-process-magenta group-hover:decoration-[3px]">View details</span>
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </Container>
          </section>
        )}

        <section className="bg-stock border-t border-ink py-16 md:py-24">
          <Container className="grid gap-10 lg:grid-cols-[1fr_minmax(0,32rem)] lg:gap-16 items-start">
            <div>
              <h2 className="font-display font-bold text-3xl sm:text-5xl text-ink">Need a price for {category.name.toLowerCase()}?</h2>
              <p className="mt-4 text-lg text-graphite leading-relaxed max-w-md">Tell us the quantity and finish and we'll send an exact quote.</p>
            </div>
            <QuoteForm testid="category-quote-form" title="" messagePrefix={`Enquiry for: ${category.name}`}
              messagePlaceholder="Quantity, finish, and your deadline" />
          </Container>
        </section>
      </main>

      {/* Sticky call bar on phones */}
      <div className="fixed bottom-0 inset-x-0 md:hidden bg-paper border-t border-ink p-3 flex gap-3 z-40">
        <a href={`tel:+${PHONE}`} className="btn btn-ink flex-1 !py-3"><Phone size={16} aria-hidden="true" /> Call</a>
        <a href={waLink(message)} target="_blank" rel="noreferrer" className="btn btn-whatsapp flex-1 !py-3"><MessageCircle size={16} aria-hidden="true" /> WhatsApp</a>
      </div>
      <div className="pb-[4.25rem] md:pb-0"><Footer /></div>

      {active && <VariantModal variant={active} onClose={() => setActive(null)} />}
    </div>
  );
}
